import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import React, {useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

/** Mot à saisir pour confirmer. Comparé sans les espaces autour et sans tenir compte de la casse. */
export const DELETE_CONFIRMATION_WORD = 'DELETE';

export const isDeleteConfirmation = (value: string): boolean => value.trim().toUpperCase() === DELETE_CONFIRMATION_WORD;

interface DeleteAccountDialogProps {
  open: boolean;
  /**
   * Nombre de brouillons côté serveur (tous, publiés compris — ce sont encore des documents).
   * `undefined` tant qu'on ne le connaît pas (chargement, erreur) : on avertit alors sans chiffre.
   */
  draftCount: number | undefined;
  /** Suppression en cours (ou session en chargement) : la confirmation est bloquée. */
  loading: boolean;
  /** 409 : seul owner d'une team avec un abonnement en cours. */
  subscriptionError: boolean;
  onClose: () => void;
  /** Rend une promesse pour que le dialogue sache quand l'appel est terminé. */
  onConfirm: () => void | Promise<void>;
}

/**
 * Confirmation de suppression de compte. N'exige plus que les brouillons soient supprimés à la
 * main : la suppression serveur les emporte avec leurs médias, on prévient seulement.
 */
export function DeleteAccountDialog({
  open,
  draftCount,
  loading,
  subscriptionError,
  onClose,
  onConfirm,
}: DeleteAccountDialogProps) {
  const {t} = useTranslation();
  const [confirmation, setConfirmation] = useState('');
  // Le 29/10/2026, une rafale d'une quarantaine de clics a envoyé autant de suppressions : le
  // bouton restait actif pendant les ~10 s de l'appel, sans rien montrer. La ref bloque aussi
  // le second clic d'un double clic, qui arrive avant le nouveau rendu.
  const confirmingRef = useRef(false);
  const [confirming, setConfirming] = useState(false);
  const busy = loading || confirming;
  const valid = isDeleteConfirmation(confirmation);
  const canConfirm = valid && !busy;

  const handleClose = () => {
    if (busy) return;
    setConfirmation('');
    onClose();
  };

  const handleConfirm = async () => {
    if (confirmingRef.current) return;
    confirmingRef.current = true;
    setConfirming(true);
    try {
      await onConfirm();
    } finally {
      confirmingRef.current = false;
      setConfirming(false);
    }
  };

  const draftsWarning =
    draftCount === undefined
      ? t('settings.removeaccount.draftswarningunknown')
      : draftCount > 0
        ? t('settings.removeaccount.draftswarning', {count: draftCount})
        : undefined;

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{t('settings.removeaccount.dialogtitle')}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t('settings.removeaccount.dialogcontent')}</DialogContentText>
        {draftsWarning !== undefined && (
          <Alert severity="warning" sx={{mt: 2}} data-testid="delete-account-drafts-warning">
            {draftsWarning}
          </Alert>
        )}
        <TextField
          autoFocus
          margin="dense"
          type="text"
          fullWidth
          variant="standard"
          label={t('settings.removeaccount.confirmlabel')}
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
          error={confirmation.trim() !== '' && !valid}
          helperText={valid ? ' ' : t('settings.removeaccount.confirmhelper')}
          sx={{mt: 2}}
        />
        {subscriptionError && (
          <DialogContentText sx={{color: 'error.main', mt: 2}}>
            {t('settings.removeaccount.subscriptionerror')}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          {t('settings.removeaccount.cancelbtn')}
        </Button>
        <Button
          onClick={() => void handleConfirm()}
          disabled={!canConfirm}
          loading={busy}
          color={canConfirm || busy ? 'secondary' : 'primary'}
        >
          {t('settings.removeaccount.confirmbtn')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
